/**
 * Foundation Identity — PersonInviteWizard.
 *
 * Wizard a 3 step per invitare un utente rispettando la separazione
 * architetturale Persona -> Account -> Assegnazioni:
 *
 *   Step 1 · Persona    : ricerca per Codice Fiscale (se esiste, collegamento)
 *   Step 2 · Assegnazioni: Azienda + Ruolo (aggiungibili piu' righe)
 *   Step 3 · Account    : email/username + metodo attivazione
 *                          (password provvisoria o link via email)
 *
 * Componente riutilizzabile da tutti i moduli della Suite.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import {
  User, Search, CheckCircle2, AlertCircle, Building2, Shield, Mail, KeyRound,
  Plus, Trash2, Copy, ArrowLeft, ArrowRight, Loader2,
} from "lucide-react";
import { toast } from "sonner";

import { validateFiscalCode, normalizeFiscalCode, validateEmail } from "./validators";
import { searchPersonByFiscalCode, inviteWizard } from "./api";

const ROLE_OPTIONS = [
  { value: "super_admin", label: "ROOT (Super Admin)", global: true },
  { value: "studio_operator", label: "Consulente Studio", global: true },
  { value: "external_consultant", label: "Consulente esterno", global: false },
  { value: "company_admin", label: "Amministratore azienda", global: false },
  { value: "company_responsible", label: "Responsabile azienda", global: false },
  { value: "employee", label: "Dipendente", global: false },
];

const STEPS = [
  { key: "person", label: "Persona", icon: User },
  { key: "assignments", label: "Assegnazioni", icon: Building2 },
  { key: "account", label: "Account", icon: KeyRound },
];

const initialState = () => ({
  step: 0,
  // step 1
  cf: "",
  cfChecked: false,
  personExisting: null, // { id, first_name, last_name, email, ... } se trovata
  firstName: "",
  lastName: "",
  personEmail: "",
  personPhone: "",
  // step 2
  assignments: [{ company_id: "", role: "company_admin" }],
  // step 3
  accountEmail: "",
  accountUsername: "",
  activationMethod: "password",
  // meta
  submitting: false,
  searching: false,
  result: null,
  error: null,
});

export default function PersonInviteWizard({
  open, onOpenChange, companies = [], allowedRoles, defaultCompanyId, onCreated,
}) {
  const [state, setState] = useState(initialState);

  const set = (patch) => setState((s) => ({ ...s, ...patch }));

  // Reset a ogni apertura
  useEffect(() => {
    if (open) {
      setState({ ...initialState(), assignments: [{ company_id: defaultCompanyId || "", role: "company_admin" }] });
    }
  }, [open, defaultCompanyId]);

  const roleOptions = useMemo(() => {
    if (!allowedRoles || allowedRoles.length === 0) return ROLE_OPTIONS;
    return ROLE_OPTIONS.filter((r) => allowedRoles.includes(r.value));
  }, [allowedRoles]);

  const cfValidation = useMemo(() => validateFiscalCode(state.cf), [state.cf]);

  const canProceedStep1 = state.cfChecked
    && cfValidation.valid
    && (state.personExisting || (state.firstName.trim() && state.lastName.trim()));
  const canProceedStep2 = state.assignments.length > 0
    && state.assignments.every((a) => a.role && (isGlobalRole(a.role) || a.company_id));
  const canSubmit = state.accountEmail && validateEmail(state.accountEmail).valid;

  const progressValue = ((state.step + 1) / STEPS.length) * 100;

  // Step 1 — verifica CF su backend
  const handleCheckCf = async () => {
    const v = validateFiscalCode(state.cf);
    if (!v.valid) {
      toast.error("Codice Fiscale non valido", { description: v.error });
      return;
    }
    set({ searching: true, error: null });
    try {
      const res = await searchPersonByFiscalCode(v.normalized);
      if (res.count > 0) {
        const p = res.items[0];
        set({
          personExisting: p,
          firstName: p.first_name || "",
          lastName: p.last_name || "",
          personEmail: p.email || "",
          personPhone: p.phone || "",
          accountEmail: p.account?.email || p.email || "",
          cfChecked: true,
          cf: v.normalized,
        });
      } else {
        set({ personExisting: null, cfChecked: true, cf: v.normalized });
      }
    } catch (e) {
      const err = e?.response?.data?.detail || "Errore di rete";
      set({ error: err });
      toast.error("Ricerca fallita", { description: String(err) });
    } finally {
      set({ searching: false });
    }
  };

  const updateAssignment = (idx, patch) => {
    setState((s) => {
      const copy = [...s.assignments];
      copy[idx] = { ...copy[idx], ...patch };
      return { ...s, assignments: copy };
    });
  };

  const addAssignment = () => {
    setState((s) => ({
      ...s,
      assignments: [...s.assignments, { company_id: "", role: "company_admin" }],
    }));
  };

  const removeAssignment = (idx) => {
    setState((s) => {
      if (s.assignments.length <= 1) return s;
      return { ...s, assignments: s.assignments.filter((_, i) => i !== idx) };
    });
  };

  const goNext = () => set({ step: Math.min(state.step + 1, STEPS.length - 1) });
  const goBack = () => set({ step: Math.max(state.step - 1, 0) });

  const submit = async () => {
    set({ submitting: true, error: null });
    try {
      const emailValid = validateEmail(state.accountEmail);
      if (!emailValid.valid) {
        toast.error("Email non valida", { description: emailValid.error });
        set({ submitting: false });
        return;
      }
      const body = {
        person: {
          fiscal_code: state.cf,
          first_name: state.firstName.trim() || state.personExisting?.first_name,
          last_name: state.lastName.trim() || state.personExisting?.last_name,
          email: state.personEmail.trim() || null,
          phone: state.personPhone.trim() || null,
        },
        assignments: state.assignments.map((a) => ({
          company_id: isGlobalRole(a.role) ? null : a.company_id,
          role: a.role,
        })),
        account: {
          email: emailValid.normalized,
          username: state.accountUsername.trim() || null,
          activation_method: state.activationMethod,
        },
      };
      const res = await inviteWizard(body);
      set({ result: res, submitting: false });
      onCreated?.(res);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail
        : (detail?.detail || detail?.step ? `${detail.step || ""}: ${detail.detail || JSON.stringify(detail)}` : "Errore imprevisto");
      set({ error: msg, submitting: false });
      toast.error("Creazione fallita", { description: msg });
    }
  };

  const close = () => {
    onOpenChange(false);
  };

  // ────────── Render ──────────
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent formDialog className="max-w-2xl" data-testid="person-invite-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-primary" />
            Nuovo utente della Suite
          </DialogTitle>
          <DialogDescription>
            Il sistema crea prima la Persona (Codice Fiscale), poi le sue assegnazioni e infine l'account di accesso.
          </DialogDescription>
        </DialogHeader>

        <StepIndicator step={state.step} />
        <Progress value={progressValue} className="h-1" />

        <div className="mt-4">
          {state.result ? (
            <ResultView result={state.result} onClose={close} />
          ) : state.step === 0 ? (
            <StepPerson
              state={state} set={set}
              onCheckCf={handleCheckCf}
              cfValidation={cfValidation}
            />
          ) : state.step === 1 ? (
            <StepAssignments
              state={state}
              updateAssignment={updateAssignment}
              addAssignment={addAssignment}
              removeAssignment={removeAssignment}
              companies={companies}
              roleOptions={roleOptions}
            />
          ) : (
            <StepAccount state={state} set={set} />
          )}
        </div>

        {!state.result && (
          <DialogFooter className="mt-4">
            {state.step > 0 && (
              <Button variant="ghost" onClick={goBack} disabled={state.submitting}
                        data-testid="wizard-back-btn">
                <ArrowLeft className="mr-2 h-4 w-4" /> Indietro
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="ghost" onClick={close} disabled={state.submitting}
                      data-testid="wizard-cancel-btn">
              Annulla
            </Button>
            {state.step < STEPS.length - 1 ? (
              <Button
                onClick={goNext}
                disabled={state.step === 0 ? !canProceedStep1 : !canProceedStep2}
                data-testid="wizard-next-btn"
              >
                Avanti <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            ) : (
              <Button
                onClick={submit}
                disabled={!canSubmit || state.submitting}
                data-testid="wizard-submit-btn"
              >
                {state.submitting ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Creazione…</>
                ) : (
                  <>Crea utente</>
                )}
              </Button>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function isGlobalRole(role) {
  return role === "super_admin" || role === "studio_operator";
}

function StepIndicator({ step }) {
  return (
    <div className="flex items-center justify-between px-1 py-2">
      {STEPS.map((s, i) => {
        const Icon = s.icon;
        const active = i === step;
        const done = i < step;
        return (
          <div key={s.key} className="flex flex-1 items-center gap-2">
            <div
              className={
                "flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold " +
                (done ? "bg-emerald-600 text-white"
                  : active ? "bg-primary text-white"
                  : "bg-slate-200 text-slate-500")
              }
            >
              {done ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-4 w-4" />}
            </div>
            <span className={"text-sm " + (active ? "font-semibold" : "text-slate-500")}>
              {i + 1}. {s.label}
            </span>
            {i < STEPS.length - 1 && (
              <div className="flex-1 border-t border-dashed mx-2 border-slate-300" />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 1 · Persona (ricerca CF)
// ═══════════════════════════════════════════════════════════════════════════════
function StepPerson({ state, set, onCheckCf, cfValidation }) {
  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium">Codice Fiscale <span className="text-red-500">*</span></label>
        <div className="mt-1 flex gap-2">
          <Input
            value={state.cf}
            onChange={(e) => set({ cf: normalizeFiscalCode(e.target.value), cfChecked: false, personExisting: null })}
            placeholder="RSSMRA80A01H501U"
            maxLength={16}
            data-testid="wizard-cf-input"
            className="uppercase font-mono"
          />
          <Button
            onClick={onCheckCf}
            disabled={!cfValidation.valid || state.searching}
            data-testid="wizard-cf-search-btn"
          >
            {state.searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="ml-2">Cerca</span>
          </Button>
        </div>
        {!cfValidation.valid && state.cf && (
          <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" /> {cfValidation.error}
          </p>
        )}
      </div>

      {state.cfChecked && state.personExisting && (
        <Alert className="bg-blue-50 border-blue-200" data-testid="wizard-person-existing-alert">
          <CheckCircle2 className="h-4 w-4 text-blue-600" />
          <AlertTitle>Persona gia' registrata</AlertTitle>
          <AlertDescription className="space-y-2">
            <div>
              <b>{state.personExisting.first_name} {state.personExisting.last_name}</b>
              {" — "}<span className="font-mono">{state.personExisting.fiscal_code}</span>
            </div>
            {state.personExisting.account && (
              <div className="text-xs">
                Account esistente: <b>{state.personExisting.account.username}</b> — verranno aggiunte solo le nuove assegnazioni.
              </div>
            )}
            {state.personExisting.assignments && state.personExisting.assignments.length > 0 && (
              <div className="text-xs">
                Assegnazioni attuali: {state.personExisting.assignments.map((a) => a.role).join(", ")}
              </div>
            )}
          </AlertDescription>
        </Alert>
      )}

      {state.cfChecked && !state.personExisting && (
        <Alert className="bg-amber-50 border-amber-200" data-testid="wizard-person-new-alert">
          <User className="h-4 w-4 text-amber-600" />
          <AlertTitle>Nuova Persona</AlertTitle>
          <AlertDescription>
            Nessuna Persona trovata con questo CF. Compila i dati anagrafici.
          </AlertDescription>
        </Alert>
      )}

      {state.cfChecked && (
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm font-medium">Nome <span className="text-red-500">*</span></label>
            <Input
              value={state.firstName}
              onChange={(e) => set({ firstName: e.target.value })}
              disabled={!!state.personExisting}
              data-testid="wizard-first-name"
            />
          </div>
          <div>
            <label className="text-sm font-medium">Cognome <span className="text-red-500">*</span></label>
            <Input
              value={state.lastName}
              onChange={(e) => set({ lastName: e.target.value })}
              disabled={!!state.personExisting}
              data-testid="wizard-last-name"
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium">Email persona (facoltativa)</label>
            <Input
              type="email"
              value={state.personEmail}
              onChange={(e) => set({ personEmail: e.target.value })}
              disabled={!!state.personExisting}
              placeholder="email@dominio.it"
              data-testid="wizard-person-email"
            />
          </div>
          <div className="col-span-2">
            <label className="text-sm font-medium">Telefono (facoltativo)</label>
            <Input
              value={state.personPhone}
              onChange={(e) => set({ personPhone: e.target.value })}
              disabled={!!state.personExisting}
              placeholder="+39 333 1234567"
              data-testid="wizard-person-phone"
            />
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 2 · Assegnazioni
// ═══════════════════════════════════════════════════════════════════════════════
function StepAssignments({ state, updateAssignment, addAssignment, removeAssignment, companies, roleOptions }) {
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Definisci una o piu' <b>assegnazioni</b>: coppie Azienda + Ruolo che la Persona ricoprira'.
        I ruoli globali (ROOT, Consulente Studio) non richiedono azienda.
      </p>
      {state.assignments.map((a, idx) => {
        const globalRole = isGlobalRole(a.role);
        return (
          <div
            key={idx}
            className="rounded-md border p-3 space-y-2"
            data-testid={`wizard-assignment-row-${idx}`}
          >
            <div className="flex items-center justify-between">
              <Badge variant="secondary">Assegnazione #{idx + 1}</Badge>
              {state.assignments.length > 1 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeAssignment(idx)}
                  data-testid={`wizard-assignment-remove-${idx}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium flex items-center gap-1">
                  <Shield className="h-3 w-3" /> Ruolo
                </label>
                <Select value={a.role} onValueChange={(v) => updateAssignment(idx, { role: v, ...(isGlobalRole(v) ? { company_id: "" } : {}) })}>
                  <SelectTrigger data-testid={`wizard-assignment-role-${idx}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {roleOptions.map((r) => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-xs font-medium flex items-center gap-1">
                  <Building2 className="h-3 w-3" /> Azienda
                </label>
                <Select
                  value={a.company_id}
                  onValueChange={(v) => updateAssignment(idx, { company_id: v })}
                  disabled={globalRole}
                >
                  <SelectTrigger data-testid={`wizard-assignment-company-${idx}`}>
                    <SelectValue placeholder={globalRole ? "Non richiesta" : "Seleziona azienda"} />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={addAssignment} data-testid="wizard-add-assignment">
        <Plus className="mr-2 h-4 w-4" /> Aggiungi un'altra assegnazione
      </Button>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STEP 3 · Account
// ═══════════════════════════════════════════════════════════════════════════════
function StepAccount({ state, set }) {
  const emailV = validateEmail(state.accountEmail);
  return (
    <div className="space-y-4">
      {state.personExisting?.account ? (
        <Alert className="bg-blue-50 border-blue-200">
          <CheckCircle2 className="h-4 w-4 text-blue-600" />
          <AlertTitle>Account gia' esistente</AlertTitle>
          <AlertDescription>
            La Persona ha gia' l'account <b>{state.personExisting.account.username}</b>.
            Verranno aggiunte solo le nuove assegnazioni.
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <div>
            <label className="text-sm font-medium">Email accesso <span className="text-red-500">*</span></label>
            <Input
              type="email"
              value={state.accountEmail}
              onChange={(e) => set({ accountEmail: e.target.value })}
              placeholder="email@dominio.it"
              data-testid="wizard-account-email"
            />
            {state.accountEmail && !emailV.valid && (
              <p className="mt-1 text-xs text-red-600">{emailV.error}</p>
            )}
          </div>
          <div>
            <label className="text-sm font-medium">Username (facoltativo)</label>
            <Input
              value={state.accountUsername}
              onChange={(e) => set({ accountUsername: e.target.value })}
              placeholder="Se vuoto, generato come nome.cognome"
              data-testid="wizard-account-username"
            />
          </div>
          <Separator />
          <div>
            <label className="text-sm font-medium block mb-2">Metodo di attivazione</label>
            <div className="grid grid-cols-1 gap-2">
              <label className={"flex gap-3 rounded-md border p-3 cursor-pointer " + (state.activationMethod === "password" ? "border-primary bg-primary/5" : "")}>
                <input
                  type="radio"
                  name="activation"
                  value="password"
                  checked={state.activationMethod === "password"}
                  onChange={() => set({ activationMethod: "password" })}
                  data-testid="wizard-activation-password"
                />
                <div>
                  <div className="font-medium flex items-center gap-2">
                    <KeyRound className="h-4 w-4" /> Password provvisoria generata automaticamente
                  </div>
                  <div className="text-xs text-slate-500">
                    Verra' mostrata a te subito dopo la creazione. L'utente dovra' cambiarla al primo accesso.
                  </div>
                </div>
              </label>
              <label className={"flex gap-3 rounded-md border p-3 cursor-pointer " + (state.activationMethod === "link" ? "border-primary bg-primary/5" : "")}>
                <input
                  type="radio"
                  name="activation"
                  value="link"
                  checked={state.activationMethod === "link"}
                  onChange={() => set({ activationMethod: "link" })}
                  data-testid="wizard-activation-link"
                />
                <div>
                  <div className="font-medium flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Link di attivazione via email
                  </div>
                  <div className="text-xs text-slate-500">
                    L'utente ricevera' un link per impostare la propria password. Se il servizio email non e' attivo, potrai comunque copiare il link.
                  </div>
                </div>
              </label>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Risultato finale
// ═══════════════════════════════════════════════════════════════════════════════
function ResultView({ result, onClose }) {
  const copy = (text, label = "Copiato negli appunti") => {
    navigator.clipboard.writeText(text);
    toast.success(label);
  };

  // v1.3.6+ · P0.5 · Blocchi copia pronti per comunicazione manuale (compatibile GI Connect).
  // Nessun invio automatico: lo Studio comunica le credenziali manualmente.
  const username = result.account?.username || "";
  const email = result.account?.email || "";
  const password = result.provisional_password || "";
  const inviteLink = result.invite_link || "";
  const fullName = `${result.person?.first_name || ""} ${result.person?.last_name || ""}`.trim();

  const credentialsBlock = [
    `Username: ${username}`,
    email ? `Email: ${email}` : null,
    password ? `Password provvisoria: ${password}` : null,
    inviteLink ? `Link di attivazione: ${inviteLink}` : null,
  ].filter(Boolean).join("\n");

  const emailBlock =
    `Oggetto: Credenziali di accesso al Gestionale Presenze\n\n` +
    `Gentile ${fullName || "Utente"},\n\n` +
    `le comunichiamo le credenziali per accedere al Gestionale Presenze:\n\n` +
    `${credentialsBlock}\n\n` +
    (password ? `Al primo accesso le verrà richiesto di impostare una nuova password personale.\n\n` : "") +
    `Per qualsiasi necessità può rispondere a questa comunicazione.\n\n` +
    `Cordiali saluti.`;

  return (
    <div className="space-y-4" data-testid="wizard-result">
      <Alert className="bg-emerald-50 border-emerald-200">
        <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        <AlertTitle>Utente creato con successo</AlertTitle>
        <AlertDescription>
          {result.person_reused
            ? "La Persona esisteva gia' ed e' stata collegata."
            : "Nuova Persona registrata in Foundation Identity."}
        </AlertDescription>
      </Alert>

      <div className="rounded-md border p-3 text-sm space-y-1">
        <div><span className="text-slate-500">Persona:</span> <b>{result.person.first_name} {result.person.last_name}</b> — <span className="font-mono">{result.person.fiscal_code}</span></div>
        {result.account && (
          <>
            <div><span className="text-slate-500">Username:</span> <b data-testid="wizard-result-username">{result.account.username}</b></div>
            <div><span className="text-slate-500">Email:</span> {result.account.email}</div>
            <div><span className="text-slate-500">Stato:</span> <Badge variant="secondary">{result.account.account_status}</Badge></div>
          </>
        )}
        <div><span className="text-slate-500">Assegnazioni create:</span> {result.assignments?.length || 0}</div>
      </div>

      {password && (
        <Alert className="bg-amber-50 border-amber-200">
          <KeyRound className="h-4 w-4 text-amber-600" />
          <AlertTitle>Password provvisoria</AlertTitle>
          <AlertDescription className="space-y-2">
            <div className="flex items-center gap-2">
              <code className="px-2 py-1 rounded bg-slate-800 text-white font-mono text-sm" data-testid="wizard-provisional-password">
                {password}
              </code>
              <Button size="sm" variant="outline" onClick={() => copy(password, "Password copiata")}
                        data-testid="wizard-copy-password-btn">
                <Copy className="h-3 w-3 mr-1" /> Copia Password
              </Button>
            </div>
            <p className="text-xs">All'utente verra' richiesto di sostituirla al primo accesso.</p>
          </AlertDescription>
        </Alert>
      )}

      {inviteLink && (
        <Alert className="bg-blue-50 border-blue-200">
          <Mail className="h-4 w-4 text-blue-600" />
          <AlertTitle>Link di attivazione</AlertTitle>
          <AlertDescription className="space-y-2">
            <div className="text-xs break-all font-mono bg-white/60 rounded p-2" data-testid="wizard-invite-link">
              {inviteLink}
            </div>
            <Button size="sm" variant="outline" onClick={() => copy(inviteLink, "Link copiato")}
                      data-testid="wizard-copy-link-btn">
              <Copy className="h-3 w-3 mr-1" /> Copia link
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {/* v1.3.6+ · P0.5 · Comunicazione manuale credenziali (GI Connect-ready) */}
      <Alert className="bg-slate-50 border-slate-200" data-testid="wizard-manual-comm-block">
        <AlertTitle>Comunicazione manuale delle credenziali</AlertTitle>
        <AlertDescription className="space-y-3">
          <p className="text-xs text-slate-700">
            Nessuna email automatica viene inviata.
            Copia il blocco corrispondente e incollalo nel canale che preferisci
            (email, chat, WhatsApp, GI Connect quando sara' disponibile).
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => copy(credentialsBlock, "Credenziali complete copiate")}
              data-testid="wizard-copy-credentials-btn"
            >
              <Copy className="h-3 w-3 mr-1" /> Copia Credenziali complete
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => copy(emailBlock, "Email completa copiata")}
              data-testid="wizard-copy-email-btn"
            >
              <Copy className="h-3 w-3 mr-1" /> Copia Email completa
            </Button>
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-slate-600 hover:text-slate-900">
              Anteprima email completa
            </summary>
            <pre className="mt-2 whitespace-pre-wrap bg-white/80 rounded p-2 border border-slate-200 font-mono text-[11px]"
                 data-testid="wizard-email-preview">{emailBlock}</pre>
          </details>
        </AlertDescription>
      </Alert>

      <DialogFooter>
        <Button onClick={onClose} data-testid="wizard-close-btn">Chiudi</Button>
      </DialogFooter>
    </div>
  );
}
